import React, { useState, useEffect } from 'react';

/**
 * IzmexCarbonCircuit - Data Lake & Analytics Pipeline
 * 
 * Multi-source data ingestion into analytics core.
 * Shows: SENSORS → INGEST → LAKE → ENGINE → DASHBOARD
 */

export function IzmexCarbonCircuit({ className = '' }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className={`relative ${className} transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ width: '260px', height: '180px' }}
    >
      <svg 
        viewBox="0 0 130 90" 
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="carbonGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <pattern id="carbonGrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(15, 118, 110, 0.08)" strokeWidth="0.3"/>
          </pattern>
        </defs>

        {/* Background grid */}
        <rect width="130" height="90" fill="url(#carbonGrid)" />

        {/* Connection paths - Layered data flow */}
        <g className="connections">
          {/* Source streams converging */}
          <path d="M 12 20 L 25 20 L 35 35" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          <path d="M 12 45 L 35 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 12 70 L 25 70 L 35 55" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          
          {/* Ingest to Lake */}
          <path d="M 50 45 L 58 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Lake to Engine */}
          <path d="M 78 45 L 88 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Engine to Output */}
          <path d="M 103 45 L 115 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Batch process line (dashed) */}
          <path d="M 68 58 L 68 75 L 95 75 L 95 52" fill="none" stroke="rgba(15, 118, 110, 0.25)" strokeWidth="0.4" strokeDasharray="2,2" />
        </g>

        {/* Data pulse */}
        <g className="data-pulse">
          <circle r="1.5" fill="#0F766E" filter="url(#carbonGlow)">
            <animateMotion dur="7s" repeatCount="indefinite">
              <mpath href="#carbonPath" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;1;0" dur="7s" repeatCount="indefinite" />
          </circle>
          <path id="carbonPath" d="M 12 45 L 42 45 L 68 45 L 95 45 L 120 45" fill="none" stroke="none" />
        </g>

        {/* Nodes with labels */}
        <g className="nodes" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {/* Data Sources */}
          <rect x="5" y="16" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="11" y="22" textAnchor="middle" fill="#0F766E" fontSize="3.5">IoT</text>
          
          <rect x="5" y="41" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.1)" stroke="rgba(15, 118, 110, 0.6)" strokeWidth="0.8" />
          <text x="11" y="47" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">API</text>
          
          <rect x="5" y="66" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="11" y="72" textAnchor="middle" fill="#0F766E" fontSize="3.5">ERP</text>
          
          {/* Ingest Layer */}
          <rect x="35" y="38" width="16" height="14" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="43" y="44" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">INGEST</text>
          <text x="43" y="49" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">kafka</text>
          
          {/* Data Lake (Core) */}
          <rect x="58" y="36" width="20" height="18" rx="2" fill="rgba(15, 118, 110, 0.15)" stroke="#0F766E" strokeWidth="1" />
          <text x="68" y="44" textAnchor="middle" fill="#0F766E" fontSize="5" fontWeight="600">LAKE</text>
          <text x="68" y="50" textAnchor="middle" fill="rgba(15, 118, 110, 0.8)" fontSize="3">delta</text>
          
          {/* Analytics Engine */}
          <rect x="88" y="38" width="16" height="14" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="96" y="44" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">CALC</text>
          <text x="96" y="49" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">spark</text>
          
          {/* Dashboard Output */}
          <rect x="115" y="40" width="10" height="10" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.45)" strokeWidth="0.6" />
          <text x="120" y="47" textAnchor="middle" fill="#0F766E" fontSize="3.5">UI</text>
        </g>

        {/* Technical annotations */}
        <g className="annotations" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <text x="68" y="62" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">12TB/day</text>
          <text x="80" y="79" textAnchor="middle" fill="rgba(15, 118, 110, 0.6)" fontSize="3">batch_sync</text>
        </g>

        {/* Status indicator */}
        <g className="status">
          <circle cx="6" cy="6" r="2.5" fill="#10B981">
            <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
          </circle>
          <text x="12" y="8" fill="#10B981" fontSize="3.5" fontWeight="600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>SYNC</text>
        </g>
      </svg>
    </div>
  );
}

export default IzmexCarbonCircuit;
